// service/SyncService.js
import axios from '../axios/axiosInstance';
import config from './Config';

class SyncService {
    constructor() {
        this.pollInterval = 5000; // 5 segundos para polling
        this.maxWaitTime = 3600000; // 1 hora (máximo esperado)
    }

    // ============================================
    // 🔄 SINCRONIZAÇÃO COMPLETA (Assíncrona)
    // ============================================

    /**
     * Dispara sincronização completa (retorna job_id)
     * POST /sync/{profile_id}/all
     */
    syncAll(profile_id, data = {}) {
        return axios.post(`${config.api.version}/sync/${profile_id}/all`, data);
    }

    /**
     * Obtém status geral (última sincronização + jobs em execução)
     * GET /sync/{profile_id}/status-all
     */
    getAllSyncStatus(profile_id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/status-all`);
    }

    // ============================================
    // 📦 SINCRONIZAÇÃO DE PRODUTOS (Assíncrona)
    // ============================================

    /**
     * Dispara sincronização de produtos (retorna job_id)
     * POST /sync/{profile_id}/produtos
     */
    syncProdutos(profile_id, data = {}) {
        return axios.post(`${config.api.version}/sync/${profile_id}/produtos`, data);
    }

    /**
     * Obtém status de um job específico
     * GET /sync/jobs/{jobId}/status
     */
    getSyncJobStatus(jobId) {
        return axios.get(`${config.api.version}/sync/jobs/${jobId}/status`);
    }

    /**
     * Obtém último status de produtos
     * GET /sync/{profile_id}/produtos/status
     * @deprecated Use getSyncJobStatus com jobId específico
     */
    getProdutosStatus(profile_id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/produtos/status`);
    }

    /**
     * Obtém histórico de sincronizações de produtos
     * GET /sync/{profile_id}/produtos/history
     * @deprecated Use getLatestJobs
     */
    getProdutosHistory(profile_id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/produtos/history`);
    }

    /**
     * Obtém detalhes de uma sincronização específica
     * GET /sync/{profile_id}/produtos/{id}
     */
    getProdutoDetails(profile_id, id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/produtos/${id}`);
    }

    /**
     * Obtém erros de uma sincronização de produtos
     * GET /sync/{profile_id}/produtos/{id}/errors
     */
    getProdutoErrors(profile_id, id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/produtos/${id}/errors`);
    }

    /**
     * Cancela sincronização de produtos em andamento
     * POST /sync/jobs/{jobId}/cancel
     * @deprecated Use cancelJob(jobId)
     */
    cancelProdutosSync(profile_id) {
        return axios.put(`${config.api.version}/sync/${profile_id}/produtos/cancel`);
    }

    /**
     * Obtém estatísticas de produtos sincronizados
     * GET /sync/{profile_id}/produtos/statistics
     */
    getProdutosStatistics(profile_id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/produtos/statistics`);
    }

    /**
     * Obtém resumo de produtos
     * GET /sync/{profile_id}/produtos/resumo
     */
    getProdutosResumo(profile_id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/produtos/resumo`);
    }

    /**
     * Lista todos os produtos sincronizados
     * GET /sync/{profile_id}/produtos
     */
    listProdutos(profile_id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/produtos`);
    }

    /**
     * Obtém produtos de uma categoria específica
     * GET /sync/{profile_id}/produtos/categoria/{categoriaId}
     */
    getProdutosByCategoria(profile_id, categoriaId) {
        return axios.get(`${config.api.version}/sync/${profile_id}/produtos/categoria/${categoriaId}`);
    }

    /**
     * Obtém produtos com estoque
     * GET /sync/{profile_id}/produtos/estoque
     */
    getProdutosComEstoque(profile_id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/produtos/estoque`);
    }

    /**
     * Obtém produtos sem imagem
     * GET /sync/{profile_id}/produtos/sem-imagem
     */
    getProdutosSemImagem(profile_id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/produtos/sem-imagem`);
    }

    /**
     * Obtém produto específico por ID
     * GET /sync/{profile_id}/produtos/{produtoId}
     */
    getProdutoById(profile_id, produtoId, empresaId) {
        return axios.get(`${config.api.version}/sync/${profile_id}/produtos/${produtoId}?empresaId=${empresaId}`);
    }

    /**
     * Verifica se um produto tem imagem
     * GET /sync/{profile_id}/produtos/{produtoId}/verificar-imagem
     */
    verificarImagemProduto(profile_id, produtoId) {
        return axios.get(`${config.api.version}/sync/${profile_id}/produtos/${produtoId}/verificar-imagem`);
    }

    /**
     * Gera URLs de imagens para produtos
     * POST /sync/{profile_id}/produtos/gerar-urls
     */
    gerarUrlsImagens(profile_id, ids) {
        return axios.post(`${config.api.version}/sync/${profile_id}/produtos/gerar-urls`, { ids });
    }

    // ============================================
    // 📂 SINCRONIZAÇÃO DE CATEGORIAS (Assíncrona)
    // ============================================

    /**
     * Dispara sincronização de categorias (retorna job_id)
     * POST /sync/{profile_id}/categorias
     */
    syncCategorias(profile_id, data = {}) {
        return axios.post(`${config.api.version}/sync/${profile_id}/categorias`, data);
    }

    /**
     * Obtém último status de categorias
     * GET /sync/{profile_id}/categorias/status
     * @deprecated Use getSyncJobStatus com jobId específico
     */
    getCategoriasStatus(profile_id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/categorias/status`);
    }

    /**
     * Obtém histórico de sincronizações de categorias
     * GET /sync/{profile_id}/categorias/history
     * @deprecated Use getLatestJobs
     */
    getCategoriasHistory(profile_id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/categorias/history`);
    }

    /**
     * Obtém detalhes de uma sincronização específica
     * GET /sync/{profile_id}/categorias/{id}
     */
    getCategoriaDetails(profile_id, id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/categorias/${id}`);
    }

    /**
     * Obtém erros de uma sincronização de categorias
     * GET /sync/{profile_id}/categorias/{id}/errors
     */
    getCategoriaErrors(profile_id, id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/categorias/${id}/errors`);
    }

    /**
     * Cancela sincronização de categorias em andamento
     * POST /sync/jobs/{jobId}/cancel
     * @deprecated Use cancelJob(jobId)
     */
    cancelCategoriasSync(profile_id) {
        return axios.put(`${config.api.version}/sync/${profile_id}/categorias/cancel`);
    }

    /**
     * Obtém estatísticas de categorias sincronizadas
     * GET /sync/{profile_id}/categorias/statistics
     */
    getCategoriasStatistics(profile_id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/categorias/statistics`);
    }

    /**
     * Lista todas as categorias sincronizadas
     * GET /sync/{profile_id}/categorias
     */
    listCategorias(profile_id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/categorias`);
    }

    /**
     * Obtém categoria específica por ID
     * GET /sync/{profile_id}/categorias/{id}
     */
    getCategoria(profile_id, id) {
        return axios.get(`${config.api.version}/sync/${profile_id}/categorias/${id}`);
    }

    // ============================================
    // 🎯 NOVOS MÉTODOS - CONTROLE DE JOBS
    // ============================================

    /**
     * Cancela um job específico
     * POST /sync/jobs/{jobId}/cancel
     */
    cancelJob(jobId) {
        return axios.post(`${config.api.version}/sync/jobs/${jobId}/cancel`);
    }

    /**
     * Obtém últimos jobs de um profile
     * GET /sync/jobs/latest/{profile_id}
     */
    getLatestJobs(profile_id, limit = 10) {
        return axios.get(`${config.api.version}/sync/jobs/latest/${profile_id}?limit=${limit}`);
    }

    // ============================================
    // 🔄 POLLING - MONITORAMENTO DE JOBS
    // ============================================

    /**
     * Faz polling de um job até que termine (completo, falha ou cancelado)
     * @param {string} jobId - ID do job a monitorar
     * @param {function} onProgress - Callback chamado a cada atualização
     * @param {number} maxWaitMs - Tempo máximo de espera (default 1h)
     * @returns {Promise} Retorna status final do job
     */
    async pollJobUntilComplete(jobId, onProgress = null, maxWaitMs = this.maxWaitTime) {
        const startTime = Date.now();

        return new Promise((resolve, reject) => {
            const pollInterval = setInterval(async () => {
                try {
                    // Verificar tempo máximo
                    if (Date.now() - startTime > maxWaitMs) {
                        clearInterval(pollInterval);
                        reject(new Error(`Job ${jobId} excedeu tempo máximo de execução (${maxWaitMs}ms)`));
                        return;
                    }

                    // Obter status do job
                    const response = await this.getSyncJobStatus(jobId);
                    const jobStatus = response.data.data || response.data;

                    // Chamar callback de progresso
                    if (onProgress && typeof onProgress === 'function') {
                        onProgress(jobStatus);
                    }

                    // Verificar se job terminou
                    const finalStatuses = ['completed', 'failed', 'cancelled'];
                    if (finalStatuses.includes(jobStatus.sync_status)) {
                        clearInterval(pollInterval);
                        resolve(jobStatus);
                    }

                } catch (error) {
                    clearInterval(pollInterval);
                    reject(error);
                }
            }, this.pollInterval);
        });
    }

    /**
     * Dispara sincronização e aguarda conclusão com polling
     * @param {string} profile_id - ID do profile
     * @param {string} type - Tipo de sync: 'all', 'categorias', 'produtos'
     * @param {object} options - Opções de sincronização
     * @param {function} onProgress - Callback para atualizações
     * @returns {Promise} Resultado final do job
     */
    async syncAndPoll(profile_id, type, options = {}, onProgress = null) {
        try {
            // 1. Disparar sincronização
            let syncResponse;

            if (type === 'all') {
                syncResponse = await this.syncAll(profile_id, options);
            } else if (type === 'produtos') {
                syncResponse = await this.syncProdutos(profile_id, options);
            } else if (type === 'categorias') {
                syncResponse = await this.syncCategorias(profile_id, options);
            } else {
                throw new Error(`Tipo de sincronização inválido: ${type}`);
            }

            const jobId = syncResponse.data.data.job_id;

            // 2. Fazer polling até conclusão
            return await this.pollJobUntilComplete(jobId, onProgress);

        } catch (error) {
            console.error(`Erro ao sincronizar ${type}:`, error);
            throw error;
        }
    }

    /**
     * Verifica se há jobs em execução
     * @param {string} profile_id - ID do profile
     * @returns {Promise<boolean>}
     */
    async hasRunningJobs(profile_id) {
        try {
            const response = await this.getAllSyncStatus(profile_id);
            const data = response.data.data || response.data;

            return (data.categorias?.isRunning || false) || (data.produtos?.isRunning || false);
        } catch (error) {
            console.error('Erro ao verificar jobs em execução:', error);
            return false;
        }
    }

    /**
     * Obtém progresso agregado de todos os jobs em execução
     * @param {string} profile_id - ID do profile
     * @returns {Promise<object>}
     */
    async getAggregatedProgress(profile_id) {
        try {
            const response = await this.getAllSyncStatus(profile_id);
            const data = response.data.data || response.data;
            const runningJobs = data.runningJobs || [];

            return {
                hasRunningJobs: runningJobs.length > 0,
                totalRunning: runningJobs.length,
                jobs: runningJobs,
                averageProgress: runningJobs.length > 0
                    ? Math.round(runningJobs.reduce((sum, j) => sum + (j.progress || 0), 0) / runningJobs.length)
                    : 0
            };
        } catch (error) {
            console.error('Erro ao obter progresso agregado:', error);
            return { hasRunningJobs: false, totalRunning: 0, jobs: [], averageProgress: 0 };
        }
    }
}

export default SyncService;